/// <reference path="../../../../../../typings/globals/angular/index.d.ts" />
'use strict';

namespace ekh {
    interface DDCResponse {
        MessageType: string;
        SessionId: string;
        Status: boolean;
    }

    interface TokenResponse {
        jwtToken: string;
        statusCode: number;
    }

    const DEVICE_DATA_COLLECTOR_URL = '/partials/home/directivetemplate?type=DeviceDataCollector';

    class DDCController {
        private MESSAGE_EVENT: string = 'message';
        private TIMEOUT: number = TRPCONFIG.Domain.ThreeDS2RetryTimeout;
        private THRESHOLD: number = TRPCONFIG.Domain.ThreeDS2RetryThreshold;
        private onReceive: Function;
        private addCommand: Function;
        private cardType: string;
        private cardNumber: string;
        private form: HTMLFormElement;
        private retryCount: number = 0;
        private messageEventListener: (event: MessageEvent) => void;

        constructor(
            private $element: JQuery,
            private paymentDetailsService: any,
            private $window: ng.IWindowService,
            private $timeout: ng.ITimeoutService
        ) {}

        $onInit(): void {
            this.setUpHandlers();
        }

        $onDestroy(): void {
            this.$window.removeEventListener(this.MESSAGE_EVENT, this.messageEventListener);
        }

        public async startGetToken(): Promise<any> {
            let jwtTokenData;
            let dfReferenceId = null;

            /*
            retry to get to get JWT token.
            _getDeviceData() returns promise which may be rejected
            because of network errors or because of timeout (this.TIMEOUT)
            */
            this.retryCount = 0;
            while(!jwtTokenData && this.retryCount < this.THRESHOLD) {
                jwtTokenData = await this._getDeviceData();
                this.retryCount++;
            }
            if (!jwtTokenData) {
                this.onReceive({dfReferenceId});
                return true;
            }

            /*
            retry to submit form with JWT token and receive ReferenceId.
            _submitForm() returns promise which may be rejected
            because of problems to load iframe from the URL or
            because of not receiving POST Message from iframe.
            */
            this.retryCount = 0;
            while(!dfReferenceId && this.retryCount < this.THRESHOLD) {
                dfReferenceId = await this._getIdReference(jwtTokenData.jwtToken);
                this.retryCount++;
            }
            this.onReceive({dfReferenceId});
        }

        private _reloadIframe(): Promise<any> {
            return new Promise((resolve, reject) => {
                let reloadTimer;
                this.$element.attr('src', '');
                this.$timeout(_ => {
                    this.$element[0].onload = _ => {
                        this.$element[0].onload = angular.noop;
                        this.$timeout.cancel(reloadTimer);
                        //below block is to ensure that iframe template is loaded successfully
                        //if 404 error it is not able to get form
                        if (this.getForm(<HTMLIFrameElement>this.$element[0])) {
                            resolve();
                        } else {
                            reject();
                        }
                    };
                    this.$element.attr('src', DEVICE_DATA_COLLECTOR_URL);
                });
                /*
                Reject iframe reloadIframePromise if its not possible
                to get template because of network problems and try again
                */
                reloadTimer = this.$timeout(_ => {
                    this.$element[0].onload = angular.noop;
                    reject(null);
                }, this.TIMEOUT);
            });
        }

        private async _getIdReference(jwtToken): Promise<any> {
            let idReference = null;
            try {
                await this._reloadIframe();
                idReference = await this._submitForm(jwtToken);
            } catch(err) {
                console.error('Error getting Id Reference', err);
            }
            return idReference;
        }

        private async _getDeviceData(): Promise<any> {
            let tokenResponse;
            try {
                tokenResponse = await this.getDDCToken();
            } catch (error) {
                console.error('Error getting device data', error);
                tokenResponse = null;
            }
            return tokenResponse;
        }

        private _completeForm(cardNumber, jwtValue): void {
            const binValue = cardNumber && cardNumber.slice(0, 6);
            if (binValue && jwtValue) {
                this.form = this.getForm(<HTMLIFrameElement>this.$element[0]);
                this.setFieldValue(this.getField(this.form, 'bin'), binValue);
                this.setFieldValue(this.getField(this.form, 'jwt'), jwtValue);
            }
        }

        private setUpHandlers(): void {
            this.addCommand({ command: this.startGetToken.bind(this)});
        }

        private _submitForm(jwtToken): Promise<any> {
            this._completeForm(this.cardNumber, jwtToken);
            return new Promise((resolve, reject) => {
                let getTimer;
                this._clearMessageListener();
                this.messageEventListener = (event: MessageEvent) => {
                    this.$timeout.cancel(getTimer);
                    console.log('Recieved event response', event);
                    if (event.origin === TRPCONFIG.Domain.CardinalCommerceUrl) {
                        const data: DDCResponse = JSON.parse(event.data);
                        console.warn('Merchant received a message:', data);
                        if (data && data.Status) {
                            resolve(data.SessionId);
                        } else {
                            reject(null);
                        }
                    }
                };
                this.$window.addEventListener(this.MESSAGE_EVENT, this.messageEventListener, false);
                getTimer = this.$timeout(_ => {
                    this._clearMessageListener();
                    reject(null);
                }, this.TIMEOUT);
                this.form.submit();
            });
        }

        private _clearMessageListener() {
            if (this.messageEventListener) {
                this.$window.removeEventListener(this.MESSAGE_EVENT, this.messageEventListener);
            }
        }

        private async getDDCToken(): Promise<TokenResponse> {
            if (!this.cardType) {
                return Promise.reject('Card details are invalid');
            }
            return await this.paymentDetailsService.getDeviceDataCollectionToken(this.cardType, this.TIMEOUT);
        }

        private getForm(iframe: HTMLIFrameElement): HTMLFormElement {
            return <HTMLFormElement>iframe.contentWindow.document.getElementById('collectionForm');
        }

        private getField(form: HTMLFormElement, className: string): HTMLInputElement {
            return <HTMLInputElement>form.getElementsByClassName(className)[0];
        }

        private setFieldValue(field: HTMLInputElement, value: string): void {
            field.value = value;
        }
    }

    angular
        .module('ekh')
        .directive('deviceDataCollector', () => {
            return {
                replace: true,
                scope: {},
                bindToController: {
                    cardNumber: '<',
                    cardType: '<',
                    addCommand: '&',
                    onReceive: '&'
                },
                controller: DDCController,
                controllerAs: 'ddc',
                template:
                    `<iframe height="1" width="1" style="display:none;" src="${DEVICE_DATA_COLLECTOR_URL}"></iframe>`
            };
        });
}
